/**
 * L'oreille de l'assistant : reconnaissance vocale du navigateur, tenue
 * ouverte tout au long d'une conversation.
 *
 * Ni `SpeechRecognition` ni son prefixe webkit ne figurent dans lib.dom :
 * l'API n'est pas standardisee, et son support varie fortement. D'ou la
 * detection a l'execution plutot qu'une dependance sur les types.
 *
 * Ce que cette classe ajoute a l'API brute, et pourquoi :
 *
 *  - **le decoupage par le silence.** L'API ne dit jamais « il a fini de
 *    parler » ; elle ne livre que des segments. On envoie apres un blanc,
 *    comme un interlocuteur decide que c'est son tour.
 *  - **le redemarrage.** `continuous` n'a aucun effet sur iOS : le moteur
 *    s'arrete de lui-meme a chaque phrase, parfois au milieu. Sans reprise
 *    automatique, le micro se ferme au bout de quelques secondes et l'on
 *    croit l'assistant plante.
 *  - **l'accumulation entre deux redemarrages.** Chaque relance remet
 *    `resultIndex` a zero : sans tampon, une phrase coupee en deux perdrait
 *    sa premiere moitie.
 */

interface Segment {
  isFinal: boolean;
  0: { transcript: string };
}

interface EvenementResultat {
  resultIndex: number;
  results: ArrayLike<Segment>;
}

interface Moteur {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: EvenementResultat) => void) | null;
  onerror: ((e: { error?: string }) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
}

type Fabrique = new () => Moteur;

function fabrique(): Fabrique | null {
  if (typeof window === 'undefined') return null;
  const f = window as unknown as { SpeechRecognition?: Fabrique; webkitSpeechRecognition?: Fabrique };
  return f.SpeechRecognition ?? f.webkitSpeechRecognition ?? null;
}

export function dicteePossible(): boolean {
  return fabrique() !== null;
}

/**
 * Le blanc au bout duquel on considere le tour fini.
 *
 * **Remonte de 750 ms a 1 s apres usage reel** : a 750 ms l'assistant coupait
 * la parole au milieu des phrases. Une hesitation ordinaire — « rendez-vous
 * chez le… dentiste » — depasse allegrement 750 ms, et le provisoire ne
 * repousse pas la minuterie puisqu'un silence ne produit aucun resultat.
 *
 * La demi-seconde ainsi rendue est largement reprise ailleurs : le micro ne se
 * ferme plus entre deux tours (voir `sourde`), ce qui economisait bien
 * davantage. Perdre la fin d'une phrase coute une reprise complete ; attendre
 * 250 ms de plus ne coute que 250 ms.
 */
const SILENCE_MS = 1000;

/** Erreurs qui arretent la conversation, par opposition a celles qu'on ignore. */
const FATALES = new Set(['not-allowed', 'service-not-allowed', 'audio-capture']);

export interface OptionsDictee {
  /** Un tour de parole complet, silence detecte. */
  onPhrase: (texte: string) => void;
  /** Ce qui est entendu au fil de l'eau, pour l'afficher pendant qu'on parle. */
  onPartiel: (texte: string) => void;
  /** Micro reellement ouvert ou non — l'ouverture n'est pas instantanee. */
  onEcoute: (ecoute: boolean) => void;
  /** Seulement les pannes dont il faut parler a l'utilisateur. */
  onErreur: (message: string) => void;
  /**
   * Decouper les tours au silence, plutot qu'au relachement du bouton.
   *
   * Laisse a `false` : c'est l'utilisateur qui dit quand il a fini, en
   * relachant. Deviner la fin d'une phrase a partir d'un blanc s'est revele
   * intenable — trop court, on coupe la parole ; trop long, on attend pour
   * rien ; et jamais au bon moment pour tout le monde.
   */
  auSilence?: boolean;
}

export class Dictee {
  private moteur: Moteur | null = null;
  /** Ce qu'on veut, par opposition a l'etat reel du moteur. */
  private voulu = false;
  /**
   * Le tour de parole, en deux morceaux.
   *
   * `courant` est reconstruit a chaque evenement depuis l'integralite de
   * `results` — donc toujours juste, meme si le moteur re-signale un segment.
   * Mais `results` repart a vide quand iOS ferme et rouvre le moteur en plein
   * milieu d'une phrase : `anterieur` garde ce qui a ete dit avant cette
   * coupure, sans quoi la premiere moitie serait perdue.
   */
  private anterieur = '';
  private courant = '';
  /**
   * Ce que le moteur croit entendre, pas encore fige.
   *
   * Il fait partie de la phrase. Quand on se tait, le silence expire souvent
   * **avant** que le moteur n'ait finalise ses derniers mots : les jeter
   * amputait la fin de chaque phrase — « rendez-vous chez le dentiste » au
   * lieu de « rendez-vous chez le dentiste mardi ».
   */
  private provisoire = '';
  /**
   * D'ou commence le tour de parole dans `results`.
   *
   * `results` est cumulatif pour toute la vie du moteur. Comme on ne ferme
   * plus celui-ci entre deux tours, il contient aussi ce que le micro a
   * entendu de la voix de synthese. Le socle marque la frontiere : tout ce qui
   * precede appartient aux tours passes ou a l'assistant, et ne doit jamais
   * remonter dans la phrase de l'utilisateur.
   */
  private socle = 0;
  /** Taille de `results` au dernier evenement, pour avancer le socle. */
  private dernierNombre = 0;
  private minuterie: number | null = null;
  /** Une seule relance en attente a la fois, sinon elles se multiplient. */
  private reprise: number | null = null;
  /**
   * Micro ouvert, mais on jette ce qu'il entend.
   *
   * C'est ce qui remplace l'arret/redemarrage entre deux tours. Fermer le
   * moteur pendant que l'assistant reflechit et parle coutait deux fois : la
   * destruction, puis surtout le demarrage — sur iOS, plusieurs centaines de
   * millisecondes pendant lesquelles le micro n'entend rien alors que
   * l'utilisateur, lui, a deja commence sa phrase. C'etait la cause des
   * premiers mots perdus.
   *
   * On laisse donc le moteur tourner d'un bout a l'autre de la conversation,
   * et on ignore ses resultats quand ce n'est pas le tour de l'utilisateur.
   */
  private sourde = false;

  constructor(private readonly options: OptionsDictee) {}

  demarrer(): void {
    this.sourde = false;
    if (this.voulu) return;
    this.voulu = true;
    this.vider();
    this.ouvrir();
  }

  /**
   * Cesse d'ecouter sans fermer le micro — le temps que l'assistant reflechisse
   * et reponde.
   *
   * Le tampon est vide a l'entree **et** a la sortie : pendant qu'il parle, le
   * micro entend la voix de synthese, et ce qu'il en transcrit ne doit en aucun
   * cas se retrouver au debut du tour suivant.
   */
  rendreSourd(): void {
    this.sourde = true;
    this.annulerMinuterie();
    this.vider();
    this.options.onPartiel('');
    this.options.onEcoute(false);
  }

  /** Rend la parole a l'utilisateur, micro deja chaud. */
  rendreAudible(): void {
    this.sourde = false;
    this.vider();
    this.options.onPartiel('');
    if (!this.voulu) return;
    // iOS a pu tuer le moteur pendant qu'on parlait : on le rouvre alors, et
    // l'on ne paie le demarrage que dans ce cas-la.
    if (this.moteur === null) this.ouvrir();
    else this.options.onEcoute(true);
  }

  arreter(): void {
    this.voulu = false;
    this.annulerMinuterie();
    if (this.reprise !== null) {
      window.clearTimeout(this.reprise);
      this.reprise = null;
    }
    this.vider();
    const m = this.moteur;
    this.moteur = null;
    if (m) {
      m.onresult = null;
      m.onerror = null;
      m.onend = null;
      m.onstart = null;
      // `abort()` coupe net ; `stop()` laisse arriver un dernier resultat,
      // qui reviendrait apres coup et relancerait un envoi deja fait.
      try {
        m.abort();
      } catch {
        // Le moteur n'avait pas demarre : rien a interrompre.
      }
    }
    this.options.onEcoute(false);
  }

  private ouvrir(): void {
    const C = fabrique();
    if (!C || !this.voulu) return;

    // Le nouveau moteur repart avec un `results` vide : ce qui a deja ete
    // entendu bascule dans `anterieur`, sinon une phrase coupee en plein
    // milieu par iOS perdrait sa premiere moitie.
    // Le provisoire compte aussi : au moment ou iOS coupe, la fin de la
    // phrase n'est encore que provisoire, et elle serait perdue.
    const garde = [this.anterieur, this.courant, this.provisoire].filter((b) => b !== '').join(' ');
    if (garde !== '') this.anterieur = garde;
    this.courant = '';
    this.provisoire = '';
    this.socle = 0;
    this.dernierNombre = 0;

    const m = new C();
    m.lang = 'fr-CA';
    // Sans effet sur iOS, mais decisif sur les navigateurs de bureau : c'est
    // la difference entre ecouter une phrase et tenir une conversation.
    m.continuous = true;
    m.interimResults = true;
    m.maxAlternatives = 1;

    m.onstart = () => {
      if (!this.sourde) this.options.onEcoute(true);
    };

    m.onresult = (e) => {
      // Ce n'est pas le tour de l'utilisateur : c'est la voix de synthese que
      // le micro entend, ou une parole qui arrive pendant la reflexion. On
      // jette, mais on retient jusqu'ou : c'est ce qui evite que l'assistant
      // se cite lui-meme au debut du tour suivant.
      if (this.sourde) {
        this.socle = e.results.length;
        return;
      }

      // On RECONSTRUIT la phrase depuis le debut de `results` a chaque
      // evenement, au lieu d'ajouter ce qui suit `resultIndex`.
      //
      // C'est la correction d'un vrai defaut : `resultIndex` ne pointe pas
      // toujours apres les segments deja definitifs, et le moteur re-signale
      // volontiers un segment qu'il a deja finalise. En accumulant, chaque
      // re-signalement recollait le meme morceau — « rendez-vous rendez-vous
      // chez le dentiste ». Reconstruire est idempotent : reentendre deux fois
      // le meme segment donne exactement le meme texte.
      let definitif = '';
      let partiel = '';
      for (let i = this.socle; i < e.results.length; i++) {
        const segment = e.results[i];
        if (!segment) continue;
        const dit = (segment[0]?.transcript ?? '').trim();
        if (dit === '') continue;
        if (segment.isFinal) definitif += (definitif === '' ? '' : ' ') + dit;
        else partiel += (partiel === '' ? '' : ' ') + dit;
      }

      this.courant = definitif;
      this.provisoire = partiel;
      this.dernierNombre = e.results.length;
      this.options.onPartiel(this.phraseEntiere());
      // Toute parole entendue, meme provisoire, repousse la fin du tour :
      // sinon une phrase longue serait coupee en plein milieu.
      this.armerMinuterie();
    };

    m.onerror = (e) => {
      const code = e?.error ?? '';
      if (FATALES.has(code)) {
        this.arreter();
        this.options.onErreur(
          code === 'audio-capture'
            ? 'Je ne trouve pas de micro sur cet appareil.'
            : 'Le micro est bloqué. Autorise-le dans les réglages du navigateur.',
        );
        return;
      }
      // « no-speech » et « aborted » sont la vie normale d'un micro ouvert :
      // les signaler ferait clignoter une erreur des qu'on se tait.
      if (code === 'network') this.options.onErreur('Le micro n’a pas pu joindre le réseau.');
    };

    m.onend = () => {
      if (this.moteur !== m) return;
      this.moteur = null;
      if (!this.voulu) {
        this.options.onEcoute(false);
        return;
      }
      // On le rouvre **meme sourd**. Il transcrira la voix de synthese, qu'on
      // jette de toute facon — mais il sera chaud quand la parole reviendra.
      // Attendre `rendreAudible` pour le rouvrir revenait a payer le
      // demarrage iOS a l'instant precis ou l'utilisateur recommence a
      // parler : c'est ce qui mangeait ses premiers mots.
      // Le moteur s'arrete tout seul apres chaque phrase sur iOS. On le
      // rouvre tant que la conversation dure ; `anterieur` garde ce
      // qui a deja ete dit. On ne signale surtout pas `onEcoute(false)` au
      // passage : ces coupures sont incessantes, et l'indicateur de micro
      // clignoterait pendant qu'on parle.
      this.ouvrir();
    };

    this.moteur = m;
    try {
      m.start();
    } catch {
      // `InvalidStateError` : un moteur precedent n'a pas encore rendu la
      // main. On retente — abandonner ici fermerait le micro pour de bon,
      // sans que rien ne le signale.
      this.moteur = null;
      this.reessayer();
    }
  }

  private reessayer(): void {
    if (this.reprise !== null || !this.voulu) return;
    this.reprise = window.setTimeout(() => {
      this.reprise = null;
      if (this.voulu && this.moteur === null) this.ouvrir();
    }, 300);
  }

  /** Le tour de parole tel qu'il se dirait, les deux morceaux recolles. */
  private phraseEntiere(): string {
    return [this.anterieur, this.courant, this.provisoire]
      .filter((b) => b !== '')
      .join(' ')
      .trim();
  }

  private vider(): void {
    this.anterieur = '';
    this.courant = '';
    this.provisoire = '';
  }

  /**
   * Clot le tour de parole sur-le-champ et rend ce qui a ete entendu.
   *
   * C'est le relachement du bouton : l'utilisateur vient de dire lui-meme
   * qu'il avait fini, ce qu'aucune minuterie ne devinera jamais aussi bien.
   * Le micro passe sourd dans la foulee, sans se fermer — il restera chaud
   * pour la fois suivante.
   */
  cloreTour(): string {
    this.annulerMinuterie();
    const phrase = this.phraseEntiere();
    // Tout ce que le moteur a produit appartient a ce tour : on avance le
    // socle pour qu'il ne reapparaisse jamais. `max` protege du cas ou le
    // relachement arrive avant le moindre resultat.
    this.socle = Math.max(this.socle, this.dernierNombre);
    this.vider();
    this.sourde = true;
    this.options.onPartiel('');
    this.options.onEcoute(false);
    return phrase;
  }

  private armerMinuterie(): void {
    if (this.options.auSilence !== true) return;
    this.annulerMinuterie();
    this.minuterie = window.setTimeout(() => {
      this.minuterie = null;
      const phrase = this.phraseEntiere();
      if (phrase === '') return;
      // Le tour est clos : tout ce qui est dans `results` lui appartient et ne
      // doit plus jamais reapparaitre. Sans cela, le tour suivant renverrait
      // la phrase precedente collee devant la nouvelle.
      this.socle = this.dernierNombre;
      this.vider();
      this.options.onPartiel('');
      this.options.onPhrase(phrase);
    }, SILENCE_MS);
  }

  private annulerMinuterie(): void {
    if (this.minuterie === null) return;
    window.clearTimeout(this.minuterie);
    this.minuterie = null;
  }
}
